import type { InvoiceReadiness } from '@/api/taxDocuments';

/**
 * What the pre-flight check has to say, if anything.
 *
 * <p>
 * <strong>Its own tested module because the interesting part is when it stays quiet.</strong> A
 * notice that cries about a setting which could not change any figure teaches the shop that this
 * banner is noise — and the banner's whole job is to be believed on the day it is real. So the
 * silences here are the cases worth pinning: an unregistered shop is not warned about tax rates, and
 * a fully configured branch says nothing at all.
 * </p>
 */

export interface ReadinessWarning {
  /** `danger` when issuing would fail outright; `warning` when only part of the loop is blocked. */
  tone: 'danger' | 'warning';
  title: string;
  detail: string;
}

/**
 * The one thing to say, or null when there is nothing.
 *
 * <p>
 * <strong>One warning rather than a list, and the most blocking one wins.</strong> A branch with no
 * state code cannot invoice at all, so telling it about eleven unpriced designs in the same breath
 * buries the sentence that matters — and fixing the designs would change nothing until the state
 * code is set. A stack of banners over a register is also how a screen stops being read.
 * </p>
 */
export function readinessWarning(readiness: InvoiceReadiness): ReadinessWarning | null {
  // Nothing can be invoiced without a place of supply, and only a registered shop needs one: a bill
  // of supply charges no tax and so has no state to attribute it to.
  if (readiness.isRegistered && !readiness.hasPlaceOfSupply) {
    return {
      tone: 'danger',
      title: 'This branch has no GST state code',
      detail:
        'Nothing can be invoiced until it is set, and issuing garments will be refused. Set it on the branch — or correct the GSTIN, which the state code is read from when the box is empty.',
    };
  }

  // Without the series the *whole issue* rolls back, and the failure names numbering rather than
  // anything a clerk would connect to invoicing.
  if (!readiness.hasInvoiceSeries) {
    return {
      tone: 'danger',
      title: 'No invoice numbering series for this branch and year',
      detail:
        'Issuing garments will be refused with a message about numbering. Add an INV series for this branch in the current financial year.',
    };
  }

  if (readiness.designsWithoutTaxRate > 0) {
    const count = readiness.designsWithoutTaxRate;

    return {
      tone: 'danger',
      title: `${count} design${count === 1 ? '' : 's'} cannot be invoiced`,
      detail: `${names(readiness)} No GST rate is set on the design, its category or any category above it — issuing one of these will be refused at the counter.`,
    };
  }

  // Settlement only, so it is not the same order of problem: the shop can still hire garments out.
  if (!readiness.hasNoteSeries) {
    return {
      tone: 'warning',
      title: 'No credit/debit note series for this branch and year',
      detail:
        'Hiring out is unaffected, but settling a deposit with a late fee or damage to charge will be refused. Add a CDN series in the current financial year.',
    };
  }

  return null;
}

/**
 * The examples as a sentence, so the count is actionable rather than a number to go hunting behind.
 *
 * The server caps the list; where it did, the sentence says "and more" rather than implying the
 * named ones are all of them.
 */
function names(readiness: InvoiceReadiness): string {
  if (readiness.examples.length === 0) {
    return '';
  }

  const listed = readiness.examples.map((design) => design.name).join(', ');
  const more = readiness.designsWithoutTaxRate - readiness.examples.length;

  return more > 0 ? `${listed} and ${more} more.` : `${listed}.`;
}
